import { Select } from '@volkswagen-onehub/components-core';
import React, { PropsWithChildren, ReactElement, useLayoutEffect, useRef, useState } from "react";
import { useController, useFormContext } from 'react-hook-form';
import { SelectFieldOption } from './SelectFieldOption';

export interface SelectFieldProps {
  name: string;
  label: string;
  hint?: string;
  options: SelectFieldOption[];
  required?: boolean;
  isFloating?: boolean;
  hideAsterisk?: boolean;
  onChange?(e: React.SyntheticEvent<HTMLElement>): void;
  onFocus?(e: React.SyntheticEvent<HTMLElement>): void;
  onOpen?(): void;
  onClose?(): void;
}

export function SelectField({
  name,
  label,
  hint,
  required,
  children,
  options,
  isFloating,
  hideAsterisk,
  onChange,
  onFocus,
  onOpen,
  onClose,
  ...rest
}: PropsWithChildren<SelectFieldProps>): ReactElement {
  const { control } = useFormContext();
  const {
    field: { value, onChange: onFieldChange, onBlur },
    fieldState: { error, invalid },
    formState: { isSubmitted },
  } = useController({
    name,
    control,
  });

  const [isOpen, setIsOpen] = useState(false);

  const selectInputRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (!hideAsterisk) return;
    if (!selectInputRef.current) return;

    const labelSpans = selectInputRef.current.querySelectorAll('label span');
    labelSpans.forEach(span => {
      if (span.textContent) {
        span.textContent = span.textContent.replace(/\s*\*$/, '');
      }
    });
  }, []);


  const handleFocus = (e: React.SyntheticEvent<HTMLElement>) => {
    if (!isOpen) {
      setIsOpen(true);
      onOpen?.();
    }
    onFocus?.(e);
  };

  const showError = isSubmitted && invalid;

  if (showError) {
    return (
      <div ref={selectInputRef}>
        <Select
          label={label}
          onChange={(e) => {
            onFieldChange(e);
            onChange?.(e);
            (e.target as HTMLSelectElement).blur();
          }}
          onFocus={handleFocus}
          onBlur={onBlur}
          value={value}
          required={required}
          isFloating={isFloating}
          appearance="error"
          message={error?.message ?? ''}
          {...rest}>
          {options?.map((option: SelectFieldOption) => (
            <option value={option.value} key={option.key}>
              {option.label}
            </option>
          ))}
        </Select>
      </div>
    );
  }

  return (
    <div ref={selectInputRef}>
      <Select
        label={label}
        onChange={(e) => {
          onFieldChange(e);
          onChange?.(e);
          (e.target as HTMLSelectElement).blur();
        }}
        onFocus={handleFocus}
        onBlur={onBlur}
        value={value}
        required={required}
        isFloating={isFloating}
        {...rest}>
        {options?.map((option: SelectFieldOption) => (
          <option value={option.value} key={option.key}>
            {option.label}
          </option>
        ))}
      </Select>
    </div>
  );
}
